import { motion, useAnimation } from "framer-motion";
import { Box, Loading, Stack, styled } from "prism-react";
import React, { useEffect } from "react";

const LoadingBar = styled(Box, {
  position: "fixed",
  top: 0,
  left: 0,
  height: "$1",
  background: "$accent-secondary-base",
});

let prevLoadingMessage: React.ReactNode = null;

export interface BlockingLoaderProps {
  children?: React.ReactNode;
  /**
   * Progress from 0 to 100
   */
  loadingBarProgress?: number;
  /**
   * How fast it should creep up to the next goal (loadingBarEnd or current progress + 20%). Defaults to 8 (seconds)
   */
  loadingBarSpeed?: number;

  /**
   * What the progress bar should creep towards
   */
  loadingBarEnd?: number;

  /**
   * Where to start the loading bar from (otherwise it will animate from 0)
   */
  loadingBarStart?: number;

  childrenKey?: string | number;
}

export const BlockingLoader: React.FC<BlockingLoaderProps> = ({
  children,
  loadingBarProgress,
  loadingBarSpeed = 8,
  loadingBarStart,
  loadingBarEnd,
}) => {
  const controlsBarAnimation = useAnimation();
  const controlsPrevMessageAnimation = useAnimation();
  const controlsCurrentMessageAnimation = useAnimation();

  useEffect(() => {
    if (loadingBarProgress == null) {
      return;
    }

    if (loadingBarStart != null) {
      controlsBarAnimation.set({
        width: loadingBarStart + "%",
      });
    }
    controlsBarAnimation.start({
      width: loadingBarProgress + "%",
    });

    const timeout = setTimeout(() => {
      // Now show a slow moving spinner
      controlsBarAnimation.start({
        width: Math.min(100, loadingBarEnd ?? loadingBarProgress + 20) + "%",
        transition: { duration: loadingBarSpeed },
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [loadingBarProgress, loadingBarSpeed, loadingBarStart]);

  useEffect(() => {
    if (!prevLoadingMessage) {
      prevLoadingMessage = children;

      controlsCurrentMessageAnimation.start({
        opacity: 1,
        transform: "translateY(0px) scale(1)",
      });
    }

    const timer = setTimeout(() => {
      prevLoadingMessage = children;

      controlsPrevMessageAnimation.start({
        opacity: 0,
        transform: "translateY(-16px) scale(.96)",
      });

      controlsCurrentMessageAnimation.start({
        opacity: 1,
        transform: "translateY(0px) scale(1)",
      });
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [children, prevLoadingMessage]);

  return (
    <Stack
      css={{
        position: "relative",
        height: "100%",
      }}
    >
      {loadingBarProgress !== null && (
        <LoadingBar
          animate={controlsBarAnimation}
          as={motion.div}
          layoutId="loading-bar"
        />
      )}

      <Box
        css={{
          position: "fixed",
          flexGrow: 1,
          alignItems: "center",
          display: "flex",
          height: "100%",
          flexDirection: "column",
          justifyContent: "center",

          inset: 0,
          zIndex: 999,
        }}
      >
        <Loading size={11} />

        <Box
          css={{
            position: "relative",
            textAlign: "center",
            minWidth: "300px",
            px: "$2",
            paddingTop: "$6",

            fontSize: "$13",
            color: "$neutral-fg-high",
          }}
        >
          <Box css={{ minHeight: 16, position: "relative" }}>
            <motion.div
              animate={controlsPrevMessageAnimation}
              initial={{ transform: "translateY(0px) scale(1)" }}
              style={{ position: "absolute", inset: 0 }}
            >
              {prevLoadingMessage}
            </motion.div>

            <motion.div
              animate={controlsCurrentMessageAnimation}
              initial={{
                opacity: 0,
                transform: "translateY(16px) scale(.96)",
              }}
              style={{
                position: "absolute",
                inset: 0,
              }}
            >
              {children}
            </motion.div>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
